import { Typography } from "@/components/atoms";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";

export const InstitutionCard = React.memo(function InstitutionCard({
  title,
  subtitle,
  imageUrl,
  subtitleColor,
  height,
  onPress,
}: Readonly<{
  title: string;
  subtitle?: string;
  imageUrl?: string;
  subtitleColor: string;
  height: number;
  onPress?: () => void;
}>) {
  const card = (
    <View style={[styles.card, { height }]}>
      {imageUrl ? (
        <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="cover" />
      ) : null}
      <LinearGradient
        pointerEvents="none"
        // Figma spec: linear-gradient(0deg, rgba(12,10,8,0.92) 0%,
        // rgba(12,10,8,0) 100%) — CSS 0deg goes bottom-to-top, matching
        // expo-linear-gradient's default top-to-bottom direction reversed:
        // colors[0] (top) = CSS's 100% stop, colors[1] (bottom) = CSS's 0%.
        colors={["rgba(12,10,8,0)", "rgba(12,10,8,0.92)"]}
        locations={[0, 1]}
        style={StyleSheet.absoluteFillObject}
      />
      <View style={styles.textWrap}>
        <Typography variant="subhead" color="white" numberOfLines={2} style={styles.title}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="caption"
            numberOfLines={1}
            style={[styles.subtitle, { color: subtitleColor }]}
          >
            {subtitle}
          </Typography>
        ) : null}
      </View>
    </View>
  );

  if (!onPress) {
    return card;
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      style={styles.pressable}
    >
      {card}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  // No static height — comes from getInstitutionCardHeight(width) via the
  // `height` prop, so it varies by device tier (phone vs tablet/iPad).
  // flex: 1 (not a percentage width) guarantees an even 2-per-row split
  // regardless of the row's gap size.
  pressable: {
    flex: 1,
  },
  card: {
    flex: 1,
    borderRadius: 18,
    overflow: "hidden",
    justifyContent: "flex-end",
    backgroundColor: "#2A2A2A",
  },
  image: {
    ...StyleSheet.absoluteFillObject,
    width: undefined,
    height: undefined,
  },
  textWrap: {
    // Logical start edge: bottom-left in English, bottom-right in Arabic,
    // independent of textAlign (which only aligns within the text's own box).
    alignItems: "flex-start",
    paddingTop: 10,
    paddingRight: 12,
    paddingBottom: 12,
    paddingLeft: 12,
    gap: 2,
  },
  // Figma: Font/Size/2XS (13) Regular, LH 100% (= fontSize), letter-spacing 0.
  // "auto" follows the text's own direction, so Arabic titles right-align.
  title: {
    textAlign: "auto",
    fontFamily: "Matter-Regular",
    fontWeight: "400",
    fontSize: 13,
    lineHeight: 13,
    letterSpacing: 0,
  },
  subtitle: {
    textAlign: "auto",
  },
});

export default InstitutionCard;
